import 'services/ThemeService';
import 'toast-eduzz/dist/toast-eduzz.min.css';

import AlertComponent from 'components/AlertComponent';
import LoaderComponent from 'components/LoaderComponent';
import Scripts from 'components/Scripts';
import { blue } from '@material-ui/core/colors';
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import React from 'react';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';

import CartPage from './components/Cart';
import ToastProducer from './components/ToastProducer';
import CssBaseline from '@material-ui/core/CssBaseline';
import Error from './components/Error';
import Captcha from './components/Captcha';
import InternetStatus from 'components/InternetStatus';
import Shopback from 'components/Scripts/Shopback';

import * as Sentry from '@sentry/browser';
import ShopbackClick from 'components/Scripts/Shopback/ShopbackClick';
import Pixel from './components/Pixel';
import ThankYou from './components/ThankYou';
import { WithStyles } from 'shared/decorators/withStyles';

const theme = createMuiTheme({
  typography: {
    fontFamily: 'Lato, Helvetica, Arial, sans-serif',
    useNextVariants: true,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 700
  },
  palette: {
    primary: {
      50: '#e8f5e9',
      100: '#c8e6c9',
      200: '#a5d6a7',
      300: '#81c784',
      400: '#66bb6a',
      500: '#4caf50',
      600: '#43a047',
      700: '#388e3c',
      800: '#2e7d32',
      900: '#1b5e20',
      A100: '#b9f6ca',
      A200: '#69f0ae',
      A400: '#00e676',
      A700: '#00c853',
      contrastText: '#fff'
    },
    secondary: blue,
    background: {
      default: '#fff'
    }
  },
  overrides: {
    MuiButton: {
      root: {
        boxShadow: 'none',
        '&:active': {
          boxShadow: 'none'
        },
        '&:focus': {
          boxShadow: 'none'
        }
      },
      contained: {
        boxShadow: 'none',
        '&:active': {
          boxShadow: 'none'
        },
        '&:focus': {
          boxShadow: 'none'
        }
      }
    }
  }
});
interface IState {
  loaded: boolean;
}

@WithStyles(() => ({
  '@global': {
    'html.theme_16, html.theme_thankyou_16': {
      background: 'transparent !important',

      '& body': {
        background: 'transparent !important'
      }
    },

    '.field-error': {
      display: 'none',
      '&.error': {
        display: 'block'
      }
    },

    '.field.normal': {
      margin: 0,

      '& input, & select, & .select-input': {
        padding: '10px 8px',
        borderRadius: '2px',
        border: '1px solid #d9d9d9'
      },

      '&label[data-shrink="false"]': {
        transform: 'translate(8px, 30px) scale(1)'
      }
    },

    '.list-on-top .autocomplete-container': {
      top: 'auto !important',
      bottom: 'calc(100% - 24px) !important'
    },

    '.grow': {
      flexGrow: 1
    },

    '.card-popper .card-tooltip': {
      backgroundColor: 'transparent !important'
    },

    '.offline-alert': {
      backgroundColor: 'rgba(220, 63, 83, 0.7)',
      position: 'fixed',
      bottom: 0,
      right: 0,
      left: 0,
      width: '100%',
      padding: '10px',
      fontSize: '18px',
      color: 'white',
      textAlign: 'center',
      zIndex: 1000
    },

    '.error-alert': {
      '& .code': {
        float: 'right',
        margin: '0 0 0 10px',
        fontSize: '0.6rem',
        fontWeight: 'bold',
        color: '#999'
      }
    },

    '.theme_18': {
      '& .theme-language-bar': {
        borderBottom: '1px solid #e8e7e7'
      }
    },

    'li.planItem': {
      minHeight: '75px',
      height: 'auto',
      color: '#4e4e4e',
      padding: '0 15px',

      '& .planName': {
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        textTransform: 'capitalize',
        fontWeight: 'bold'
      }
    },

    '.planName': {
      wordBreak: 'break-word',
      whiteSpace: 'normal',
      textTransform: 'capitalize',
      fontWeight: 'bold'
    },

    '.informationsPlan': {
      wordBreak: 'break-word',
      whiteSpace: 'normal'
    }
  }
}))
class App extends React.Component<any, IState> {
  constructor(props: any) {
    super(props);
    this.state = { loaded: false };
  }

  componentDidMount() {
    this.setState({ loaded: true });
  }

  toHome = () => {
    return <Redirect to="/" />;
  };

  render() {
    const { loaded } = this.state;

    return (
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <InternetStatus />
        <Captcha />
        <LoaderComponent />
        <AlertComponent />
        <Scripts />
        <ToastProducer />
        <Shopback />
        <Pixel />
        <div id="mainContent" className="content">
          {loaded && (
            <Router>
              <Switch>
                <Route exact path="/thankyou/:id/:key" component={ThankYou} />
                <Route
                  exact
                  path="/pixel/:id"
                  render={
                    // tslint:disable-next-line:jsx-no-lambda
                    () => {
                      let iframe = false;

                      try {
                        iframe = window.location.href !== window.top.location.href;
                      } catch (err) {
                        iframe = true;
                      }

                      if (window.location.href.includes('/pixel/preload')) {
                        return null;
                      }

                      if (!iframe) {
                        window.location.href = window.location.href.replace('/pixel/', '/');
                        return null;
                      }

                      return <ShopbackClick />;
                    }
                  }
                />
                <Route
                  path="/:id"
                  render={
                    // tslint:disable-next-line:jsx-no-lambda
                    (props: any) => {
                      if (props.match.url.match(/^\/([0-9]+|c_[a-f0-9]{32})?([0-9]+|r_[a-f0-9]{32})?(\/?askaddress\/?|\/?)?$/gi)) {
                        return <CartPage />;
                      }

                      Sentry.captureException(new Error(`Invalid route: ${props.match.url}`));

                      return <Error />;
                    }
                  }
                />
                <Route exact path="/" component={Error} />
                <Route render={this.toHome} />
              </Switch>
            </Router>
          )}
        </div>
      </MuiThemeProvider>
    );
  }
}

export default App;
